import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import scrape from "../../apiConfigs/scrape";
import users from "../../apiConfigs/users";
import Dropdown from "../../designComponents/Dropdown/Dropdown";
import LabelInputField from "../../designComponents/LabelInputField/LabelInputField";
import Typography from "../../designComponents/Typography";
import requestAPI from "../../utils/requestAPI";
import {
  AcceptButton,
  ButtonsSection,
  Card,
  LabelText,
  SectionTitle,
  Title,
  TitleSection,
  ValueText,
  Wrapper,
} from "../Properties/Details/styles";
import { UserTypes } from "../Users";

type UrlType = {
  area?: number;
  bathrooms?: number;
  bedrooms?: number;
  berRating?: string;
  description?: string;
  pricing?: {
    price?: number;
    pricing?: string;
  };
  title?: string;
  address?: string;
  _id?: string;
  images?: string[];
  landlord?: string;
  location?: any;
  coordinates?: any;
  city?: string;
  country?: string;
  postalCode?: string;
  amenities?: string[];
  availableDate?: string;
};

const Scraped = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [apiError, setApiError] = useState<string>();
  const [scrapedData, setScrapedData] = useState<UrlType>();
  const [landlordData, setLandlordData] = useState<UserTypes[]>([]);
  const [selectedLandlord, setSelectedLandlord] = useState<string | number>("");
  const [search, setSearch] = useState<string | "">("");

  const handleRemoveImage = (indexToRemove: number) => {
    setScrapedData((prev) => {
      if (!prev || !prev.images) return prev;
      return {
        ...prev,
        images: prev.images.filter((_, idx) => idx !== indexToRemove),
      };
    });
  };

  const handleSave = async () => {
    if (!scrapedData) return;

    if (scrapedData.area !== undefined && scrapedData.area !== null && scrapedData.area < 0) {
      toast.error("Area cannot be a negative value.");
      return;
    }
    if (scrapedData.bedrooms !== undefined && scrapedData.bedrooms !== null && scrapedData.bedrooms < 0) {
      toast.error("Bedrooms cannot be a negative value.");
      return;
    }
    if (scrapedData.bathrooms !== undefined && scrapedData.bathrooms !== null && scrapedData.bathrooms < 0) {
      toast.error("Bathrooms cannot be a negative value.");
      return;
    }

    setIsSaving(true);
    try {
      const response = await requestAPI(
        scrape.saveScraped({
          ...scrapedData,
          url,
        })
      );
      toast.success("Property imported successfully!");
      if (response?._id) {
        setScrapedData(response);
        navigate(`/properties/${response._id}`);
      } else {
        navigate("/properties");
      }
    } catch (e: any) {
      toast.error(e.message || "Failed to save scraped property.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangeField = (field: keyof UrlType, value: any) => {
    let sanitizedValue = value;
    if (["area", "bedrooms", "bathrooms"].includes(field)) {
      if (typeof value === "number" && value < 0) {
        sanitizedValue = 0;
      }
    }
    setScrapedData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: sanitizedValue,
      };
    });
  };

  const handleScrape = async () => {
    if (isLoading || isSaving) return;
    const trimmedUrl = url.trim();

    if (!trimmedUrl) {
      setErrorMessage("Please enter a valid URL.");
      return;
    }

    if (!selectedLandlord) {
      setErrorMessage("Please select a landlord.");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await requestAPI(
        scrape.getScrape(url, selectedLandlord)
      );
      if (response && response.images && response.images.length > 20) {
        response.images = response.images.slice(0, 20);
      }
      setScrapedData(response);
      setIsLoading(false);
      toast.success("Data scraped successfully!");
    } catch (e: any) {
      setApiError(e.message);
      setIsLoading(false);
    }
  };

  const getAllLandlord = async (role: string) => {
    try {
      const response = await requestAPI(
        users.getUsers({ roles: "LANDLORD", limit: 100000, search: search })
      );
      setLandlordData(response.data);
      console.log(landlordData);
    } catch (error) {
      console.log(error, "error fetching users");
    }
  };

  useEffect(() => {
    getAllLandlord("LANDLORD");
  }, [search]);
  useEffect(() => {
    if (errorMessage) {
      toast.error(errorMessage);
      setErrorMessage("");
    }
  }, [errorMessage]);

  useEffect(() => {
    if (apiError) {
      toast.error(apiError);
      setApiError("");
    }
  }, [apiError]);
  return (
    <Wrapper style={{ paddingTop: 30 }}>
      <div>
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Title>Scrape Property</Title>

          <div>
            <LabelInputField
              placeHolder="Provide a valid URL"
              label="Enter URL to Scrape"
              name="search"
              autoComplete="off"
              disabled={isLoading || isSaving}
              style={{
                label: {
                  fontSize: "14px",
                  fontWeight: "500px",
                  color: "#555A77",
                },
                input: {
                  height: 50,
                  border: "1px solid #F1EFFB",
                  padding: 8,
                  borderRadius: 6,
                  width: "100%",
                },
                container: {
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  paddingTop: 14,
                },
              }}
              onChange={(e) => {
                setUrl(e.target.value);
              }}
            ></LabelInputField>
          </div>

          <div style={{ width: "400px" }}>
            <Dropdown
              setSearch={setSearch}
              enableSearch={true}
              disabled={isLoading || isSaving}
              placeHolder="Select a landlord"
              onSearch={(e) => {
                setSearch(e.target.value);
              }}
              options={
                landlordData
                  ? landlordData?.map((landlord) => ({
                      label: landlord?.firstName + " " + landlord?.lastName,
                      value: landlord?._id,
                    }))
                  : []
              }
              onSelect={(e) => setSelectedLandlord(e)}
              label="Select a Landlord"
            ></Dropdown>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "15px", marginTop: "20px" }}>
          <AcceptButton
            radius="md"
            style={{ marginTop: "0px" }}
            isLoading={isLoading}
            onClick={handleScrape}
            isDisabled={isLoading || isSaving}
          >
            Scrape property
          </AcceptButton>
          {isLoading && (
            <Typography style={{ color: "#806ed8", fontSize: "14px", fontWeight: "600" }}>
              Scraping in progress, wait a while...
            </Typography>
          )}
        </div>

        <AllowedSitesContainer>
          <AllowedSitesTitle>Allowed Websites to Import Properties</AllowedSitesTitle>
          <AllowedSitesDescription>
            The “Scrape Property” feature allows you to paste a link from another rental platform.
            Rentisity will automatically extract the relevant information, enabling you to review and publish.
          </AllowedSitesDescription>
          <SiteGrid>
            <SiteCard href="https://www.myhome.ie/" target="_blank" rel="noopener noreferrer">
              My Home
              <span>myhome.ie</span>
            </SiteCard>
            <SiteCard href="https://www.spotahome.com/" target="_blank" rel="noopener noreferrer">
              Spotahome
              <span>spotahome.com</span>
            </SiteCard>
            <SiteCard href="https://rentola.ie/" target="_blank" rel="noopener noreferrer">
              Rentola
              <span>rentola.ie</span>
            </SiteCard>
          </SiteGrid>
        </AllowedSitesContainer>
      </div>

      {scrapedData && (
        <Card style={{ margin: "0px" }}>
          <TitleSection
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <SectionTitle>Property Details</SectionTitle>

            {scrapedData._id ? (
              <AcceptButton
                onClick={() => navigate(`/properties/${scrapedData?._id}`)}
              >
                View Property
              </AcceptButton>
            ) : (
              <AcceptButton
                isLoading={isSaving}
                onClick={handleSave}
              >
                Import Property
              </AcceptButton>
            )}
          </TitleSection>

          {!scrapedData._id && (
            <WarningBanner>
              <span style={{ fontSize: "18px" }}>⚠️</span>
              <WarningText>
                <strong>Attention:</strong> Please review all scraped field values below carefully. Any missing fields shown as "N/A" (such as Area, Bedrooms, or Bathrooms) will be saved as default values (e.g., 0) upon import.
              </WarningText>
            </WarningBanner>
          )}

          <ButtonsSection>
            <LabelText>Address / Title</LabelText>
            {scrapedData._id ? (
              <ValueText>{scrapedData.title || scrapedData.address || "N/A"}</ValueText>
            ) : (
              <EditInput
                type="text"
                value={scrapedData.title || scrapedData.address || ""}
                onChange={(e) => {
                  handleChangeField("title", e.target.value);
                  handleChangeField("address", e.target.value);
                }}
              />
            )}
          </ButtonsSection>
          {scrapedData.images && scrapedData.images.length > 0 && (
            <ButtonsSection style={{ flexDirection: "column", gap: "10px", alignItems: "flex-start" }}>
              <LabelText>Images ({scrapedData.images.length})</LabelText>
              <ImageContainer>
                {scrapedData.images.map((imgUrl, idx) => (
                  <ImageWrapper key={idx}>
                    <ScrapedImage src={imgUrl} alt={`scraped-${idx}`} />
                    {!scrapedData._id && (
                      <RemoveImageButton
                        type="button"
                        onClick={() => handleRemoveImage(idx)}
                      >
                        ✕
                      </RemoveImageButton>
                    )}
                  </ImageWrapper>
                ))}
              </ImageContainer>
            </ButtonsSection>
          )}
          <ButtonsSection>
            <LabelText>Area (sq.ft)</LabelText>
            {scrapedData._id ? (
              <Typography style={{ color: "gray" }}>
                {scrapedData.area ? `${scrapedData.area} sq.ft` : "N/A"}
              </Typography>
            ) : (
              <EditInput
                type="number"
                min="0"
                value={scrapedData.area !== undefined && scrapedData.area !== null ? scrapedData.area : ""}
                onChange={(e) => handleChangeField("area", e.target.value ? parseFloat(e.target.value) : undefined)}
              />
            )}
          </ButtonsSection>
          <ButtonsSection>
            <LabelText>Bedrooms</LabelText>
            {scrapedData._id ? (
              <ValueText>{scrapedData.bedrooms ?? "N/A"}</ValueText>
            ) : (
              <EditInput
                type="number"
                min="0"
                value={scrapedData.bedrooms !== undefined && scrapedData.bedrooms !== null ? scrapedData.bedrooms : ""}
                onChange={(e) => handleChangeField("bedrooms", e.target.value ? parseInt(e.target.value) : undefined)}
              />
            )}
          </ButtonsSection>
          <ButtonsSection>
            <LabelText>Bathrooms</LabelText>
            {scrapedData._id ? (
              <ValueText>{scrapedData.bathrooms ?? "N/A"}</ValueText>
            ) : (
              <EditInput
                type="number"
                min="0"
                value={scrapedData.bathrooms !== undefined && scrapedData.bathrooms !== null ? scrapedData.bathrooms : ""}
                onChange={(e) => handleChangeField("bathrooms", e.target.value ? parseInt(e.target.value) : undefined)}
              />
            )}
          </ButtonsSection>
          <ButtonsSection style={{ gap: 100 }}>
            <LabelText>Description</LabelText>
            {scrapedData._id ? (
              <ValueText>{scrapedData.description || "N/A"}</ValueText>
            ) : (
              <EditTextarea
                value={scrapedData.description || ""}
                onChange={(e) => handleChangeField("description", e.target.value)}
              />
            )}
          </ButtonsSection>
        </Card>
      )}
    </Wrapper>
  );
};

const AllowedSitesContainer = styled.div`
  margin-top: 30px;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1effb 100%);
  border-radius: 12px;
  border: 1px solid #e2dff5;
  max-width: 450px;
`;

const AllowedSitesTitle = styled.h4`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #242b50;
  margin-bottom: 8px;
  margin-top: 0;
`;

const AllowedSitesDescription = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #555a77;
  line-height: 1.5;
  margin-bottom: 16px;
  margin-top: 0;
`;

const SiteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 8px;
`;

const SiteCard = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  background: white;
  border: 1px solid #e2dff5;
  border-radius: 8px;
  text-decoration: none;
  color: #806ed8;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  text-align: center;

  &:hover {
    border-color: #806ed8;
    background: #fcfbfe;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(128, 110, 216, 0.1);
    color: #634eb8;
  }
  
  span {
    font-size: 11px;
    color: #8a8ca4;
    font-weight: 400;
    margin-top: 4px;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  max-width: 100%;
`;

const ScrapedImage = styled.img`
  width: 120px;
  height: 90px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2dff5;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(128, 110, 216, 0.25);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 120px;
  height: 90px;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: rgba(220, 53, 69, 0.9);
  border: none;
  color: white;
  font-size: 11px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: background-color 0.2s ease-in-out, transform 0.2s ease-in-out;
  z-index: 10;

  &:hover {
    background-color: rgba(180, 40, 55, 1);
    transform: scale(1.1);
  }
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background-color: #fff9db;
  border: 1px solid #ffe066;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 15px;
  margin-bottom: 5px;
`;

const WarningText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #856404;
  line-height: 1.5;
  margin: 0;
  font-weight: 500;
`;

const EditInput = styled.input`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #242b50;
  border: 1px solid #e2dff5;
  border-radius: 6px;
  padding: 8px 12px;
  width: 100%;
  max-width: 400px;
  transition: border-color 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #806ed8;
    box-shadow: 0 0 0 2px rgba(128, 110, 216, 0.15);
  }
`;

const EditTextarea = styled.textarea`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #242b50;
  border: 1px solid #e2dff5;
  border-radius: 6px;
  padding: 8px 12px;
  width: 100%;
  max-width: 600px;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #806ed8;
    box-shadow: 0 0 0 2px rgba(128, 110, 216, 0.15);
  }
`;

export default Scraped;
